import { create } from 'zustand';
import { getInvoices } from '../api/generated/invoices/invoices';
import { getInvoicePositions } from '../api/generated/invoice-positions/invoice-positions';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import { downloadInvoicePdf } from '@/utils/downloadReport';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';
import i18n from '@/i18n/config';
import type { StandingPlan } from '@/utils/standingPlan';
import type {
  FacilityFlowCoreModulesInvoicesDtosInvoiceDto,
  FacilityFlowCoreModulesInvoicesDtosInvoiceListDto,
  FacilityFlowCoreModulesInvoicesDtosBillableObjectDto,
  FacilityFlowCoreModulesInvoicesWorkflowsCreateInvoiceRequest,
  FacilityFlowCoreModulesInvoicesWorkflowsCreateManualInvoiceRequest,
  FacilityFlowCoreModulesInvoicesWorkflowsBulkCreateInvoicesRequest,
  FacilityFlowCoreModulesInvoicesWorkflowsBulkCreateInvoicesResponse,
  FacilityFlowCoreModulesInvoicesWorkflowsIssueInvoiceResponse,
  FacilityFlowCoreModulesInvoicesWorkflowsUpdateInvoiceRequest,
  FacilityFlowCoreModulesInvoicesWorkflowsAddInvoicePositionRequest,
  FacilityFlowCoreModulesInvoicesWorkflowsUpdateInvoicePositionRequest,
  GetApiV1InvoicesParams,
} from '../api/generated/models';

// Type aliases
type InvoiceDto =
  FacilityFlowCoreModulesInvoicesDtosInvoiceDto;
type InvoiceListDto =
  FacilityFlowCoreModulesInvoicesDtosInvoiceListDto;
type BillableObjectDto =
  FacilityFlowCoreModulesInvoicesDtosBillableObjectDto;
type CreateInvoiceRequest =
  FacilityFlowCoreModulesInvoicesWorkflowsCreateInvoiceRequest;
type CreateManualInvoiceRequest =
  FacilityFlowCoreModulesInvoicesWorkflowsCreateManualInvoiceRequest;
type BulkCreateInvoicesRequest =
  FacilityFlowCoreModulesInvoicesWorkflowsBulkCreateInvoicesRequest;
type BulkCreateInvoicesResponse =
  FacilityFlowCoreModulesInvoicesWorkflowsBulkCreateInvoicesResponse;
type IssueInvoiceResponse =
  FacilityFlowCoreModulesInvoicesWorkflowsIssueInvoiceResponse;
type UpdateInvoiceRequest =
  FacilityFlowCoreModulesInvoicesWorkflowsUpdateInvoiceRequest;
type AddInvoicePositionRequest =
  FacilityFlowCoreModulesInvoicesWorkflowsAddInvoicePositionRequest;
type UpdateInvoicePositionRequest =
  FacilityFlowCoreModulesInvoicesWorkflowsUpdateInvoicePositionRequest;

const {
  getApiV1Invoices,
  postApiV1Invoices,
  postApiV1InvoicesManual,
  getApiV1InvoicesId,
  putApiV1InvoicesId,
  getApiV1InvoicesBillableObjects,
  getApiV1InvoicesBillableObjectsStanding,
  postApiV1InvoicesBulk,
  postApiV1InvoicesStanding,
  postApiV1InvoicesStandingBulk,
  postApiV1InvoicesIdIssue,
  postApiV1InvoicesIdSend,
  postApiV1InvoicesIdPay,
  postApiV1InvoicesIdCancel,
  deleteApiV1InvoicesId,
} = getInvoices();

const {
  postApiV1InvoicesInvoiceIdPositions,
  putApiV1InvoicesInvoiceIdPositionsPositionId,
  deleteApiV1InvoicesInvoiceIdPositionsPositionId,
  putApiV1InvoicesInvoiceIdPositionsReorder,
} = getInvoicePositions();

/** Sortable list columns — PascalCase names of the backend `InvoiceSortBy` enum. */
export type InvoiceSortBy =
  | 'Object' | 'Customer' | 'InvoiceNumber' | 'Period' | 'Status' | 'Gross' | 'DueDate';
export type InvoiceSortDir = 'Asc' | 'Desc';

export type InvoiceListQuery = Omit<GetApiV1InvoicesParams, 'sortBy' | 'sortDir'> & {
  sortBy?: InvoiceSortBy;
  sortDir?: InvoiceSortDir;
};

interface InvoiceState {
  invoices: InvoiceListDto[];
  selectedInvoice: InvoiceDto | null;
  billable: BillableObjectDto[];
  billablePeriod: { year: number; month: number } | null;
  /** Proposal list of the standing-invoice run for the chosen plan (FAFD-27); same DTO shape as `billable`. */
  standingBillable: BillableObjectDto[];
  loading: boolean;
  loadingBillable: boolean;
  loadingStandingBillable: boolean;
  saving: boolean;
  downloadingPdf: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  /** Params of the last list fetch — lets "retry" repeat it with filters and sort (FAFD-14). */
  lastQuery: InvoiceListQuery | null;

  /**
   * `params.interval` (`monthly | quarterly | yearly`) narrows the list to one invoice kind;
   * `sortBy`/`sortDir` sort server-side before paging (default Object / Asc).
   */
  fetchInvoices: (params?: InvoiceListQuery) => Promise<void>;
  getInvoiceById: (id: string) => Promise<InvoiceDto | null>;
  /** Monthly run: monthly-billed objects plus the extra work of standing-invoice objects (FAFD-27). */
  fetchBillable: (year: number, month: number) => Promise<void>;
  /** Standing run: objects billed by standing invoice with their payment plan for `plan`. */
  fetchStandingBillable: (plan: StandingPlan) => Promise<void>;
  /**
   * Payment plan preview for one plan without touching `standingBillable` (standing invoice
   * dialog of one object). Null on error (toast shown).
   */
  previewStanding: (plan: StandingPlan) => Promise<BillableObjectDto[] | null>;
  /** Creates the standing invoice of one object; returns the new invoice id or null on error. */
  createStanding: (objectId: string, plan: StandingPlan) => Promise<string | null>;
  createInvoice: (data: CreateInvoiceRequest) => Promise<string | null>;
  createManual: (data: CreateManualInvoiceRequest) => Promise<string | null>;
  bulkCreate: (
    data: BulkCreateInvoicesRequest
  ) => Promise<BulkCreateInvoicesResponse | null>;
  bulkCreateStanding: (
    plan: StandingPlan,
    objectIds: string[],
  ) => Promise<BulkCreateInvoicesResponse | null>;
  issueInvoice: (id: string) => Promise<IssueInvoiceResponse | null>;
  markSent: (id: string) => Promise<void>;
  markPaid: (id: string) => Promise<void>;
  cancelInvoice: (id: string) => Promise<void>;
  /** Drafts only (FACF-67) — issued invoices are cancelled instead. */
  deleteInvoice: (id: string) => Promise<void>;
  /** Draft-only header edit (ADR-0018, Api repo); replaces `selectedInvoice` with the returned DTO. */
  updateInvoice: (id: string, data: UpdateInvoiceRequest) => Promise<void>;
  addPosition: (invoiceId: string, data: AddInvoicePositionRequest) => Promise<void>;
  updatePosition: (
    invoiceId: string,
    positionId: string,
    data: UpdateInvoicePositionRequest,
  ) => Promise<void>;
  deletePosition: (invoiceId: string, positionId: string) => Promise<void>;
  reorderPositions: (invoiceId: string, positionIds: string[]) => Promise<void>;
  downloadPdf: (
    id: string,
    invoiceNumber?: string | null,
    billingInterval?: string | null,
  ) => Promise<void>;
}

const t = (key: string) => i18n.t(key);

function patchListItem(
  list: InvoiceListDto[],
  id: string | undefined,
  patch: Partial<InvoiceListDto>,
): InvoiceListDto[] {
  if (!id) return list;
  return list.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

/** List rows only carry the gross total, so that is all a detail refresh can patch. */
function listPatchFrom(invoice: InvoiceDto): Partial<InvoiceListDto> {
  return { totalGrossAmount: invoice.totalGrossAmount };
}

export const useInvoiceStore = create<InvoiceState>((set) => {
  // Sequence number of the latest list fetch — see fetchInvoices.
  let listRequestId = 0;
  // Same for the standing run: plan changes overlap, only the newest response may write.
  let standingRequestId = 0;

  // why: position mutations return only the position (or 204) while totals and
  // position numbers change server-side — re-fetch the invoice instead of patching.
  const refreshInvoice = async (invoiceId: string) => {
    const invoice = await getApiV1InvoicesId(invoiceId);
    set((state) => ({
      saving: false,
      selectedInvoice: invoice,
      invoices: patchListItem(state.invoices, invoiceId, listPatchFrom(invoice)),
    }));
  };

  return {
  invoices: [],
  selectedInvoice: null,
  billable: [],
  billablePeriod: null,
  standingBillable: [],
  loading: false,
  loadingBillable: false,
  loadingStandingBillable: false,
  saving: false,
  downloadingPdf: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  lastQuery: null,

  fetchInvoices: async (params?: InvoiceListQuery) => {
    // why: quick sort clicks / filter changes overlap; only the newest request may write
    // the list — an older response arriving late would otherwise show the wrong order.
    const requestId = ++listRequestId;
    // lastQuery = the newest *requested* query, so retry repeats what the user asked for.
    set({ loading: true, error: null, lastQuery: params ?? null });
    try {
      // why: the generated params type models the enums as numbers (no global string-enum
      // converter); ASP.NET binds the names from the query string — same as useObjectStore.
      const response = await getApiV1Invoices(params as GetApiV1InvoicesParams);
      if (requestId !== listRequestId) return;
      set({
        invoices: response.items || [],
        totalCount: response.totalCount || 0,
        page: response.page || 1,
        pageSize: response.pageSize || DEFAULT_PAGE_SIZE,
        loading: false,
      });
    } catch (error) {
      if (requestId !== listRequestId) return;
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  getInvoiceById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const invoice = await getApiV1InvoicesId(id);
      set({ selectedInvoice: invoice, loading: false });
      return invoice;
    } catch (error) {
      const apiError = showApiError(error);
      set({
        error: apiError.code,
        loading: false,
        selectedInvoice: null,
      });
      return null;
    }
  },

  fetchBillable: async (year: number, month: number) => {
    set({ loadingBillable: true, error: null, billablePeriod: { year, month } });
    try {
      const response = await getApiV1InvoicesBillableObjects({ year, month });
      set({
        billable: response.items || [],
        loadingBillable: false,
      });
    } catch (error) {
      const apiError = showApiError(error);
      set({
        error: apiError.code,
        loadingBillable: false,
        billable: [],
      });
    }
  },

  fetchStandingBillable: async (plan: StandingPlan) => {
    const requestId = ++standingRequestId;
    set({ loadingStandingBillable: true, error: null });
    try {
      const response = await getApiV1InvoicesBillableObjectsStanding({
        from: plan.from,
        to: plan.to,
        interval: plan.interval,
      });
      if (requestId !== standingRequestId) return;
      set({
        standingBillable: response.items || [],
        loadingStandingBillable: false,
      });
    } catch (error) {
      if (requestId !== standingRequestId) return;
      // why: the BE reports interval violations as field errors; there is no form to
      // surface them inline here, so force the toast.
      const apiError = showApiError(error, { skipIfFieldErrors: false });
      set({
        error: apiError.code,
        loadingStandingBillable: false,
        standingBillable: [],
      });
    }
  },

  previewStanding: async (plan: StandingPlan) => {
    try {
      const response = await getApiV1InvoicesBillableObjectsStanding({
        from: plan.from,
        to: plan.to,
        interval: plan.interval,
      });
      return response.items || [];
    } catch (error) {
      showApiError(error, { skipIfFieldErrors: false });
      return null;
    }
  },

  createStanding: async (objectId: string, plan: StandingPlan) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1InvoicesStanding({
        objectId,
        serviceDateFrom: plan.from,
        serviceDateTo: plan.to,
        paymentInterval: plan.interval,
      });
      set({ saving: false });
      useToastStore.getState().addToast(t('invoices:messages.created'), 'success');
      return response.id ?? null;
    } catch (error) {
      // why: period / offer violations arrive as field errors; the dialog has no matching
      // inputs for all of them, so force the toast.
      const apiError = showApiError(error, { skipIfFieldErrors: false });
      set({ error: apiError.code, saving: false });
      return null;
    }
  },

  createInvoice: async (data: CreateInvoiceRequest) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1Invoices(data);
      set({ saving: false });
      useToastStore.getState().addToast(t('invoices:messages.created'), 'success');
      return response.id ?? null;
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  createManual: async (data: CreateManualInvoiceRequest) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1InvoicesManual(data);
      set({ saving: false });
      useToastStore.getState().addToast(t('invoices:messages.created'), 'success');
      return response.id ?? null;
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      return null;
    }
  },

  bulkCreate: async (data: BulkCreateInvoicesRequest) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1InvoicesBulk(data);
      set({ saving: false });
      return response;
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  bulkCreateStanding: async (plan: StandingPlan, objectIds: string[]) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1InvoicesStandingBulk({
        serviceDateFrom: plan.from,
        serviceDateTo: plan.to,
        paymentInterval: plan.interval,
        objectIds,
      });
      set({ saving: false });
      return response;
    } catch (error) {
      // why: invoices.standingPeriodInvalid / objectNotBilledYearly arrive as field
      // errors, which showApiError would otherwise swallow — the run page has no form.
      const apiError = showApiError(error, { skipIfFieldErrors: false });
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  issueInvoice: async (id: string) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1InvoicesIdIssue(id);
      const patch: Partial<InvoiceDto> = {
        status: 'Issued',
        invoiceNumber: response.invoiceNumber,
        issueDate: response.issueDate,
        dueDate: response.dueDate,
      };
      set((state) => ({
        saving: false,
        selectedInvoice: state.selectedInvoice
          ? { ...state.selectedInvoice, ...patch }
          : state.selectedInvoice,
        invoices: patchListItem(state.invoices, id, patch),
      }));
      useToastStore.getState().addToast(t('invoices:messages.issued'), 'success');
      return response;
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  markSent: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await postApiV1InvoicesIdSend(id);
      const patch: Partial<InvoiceDto> = { status: 'Sent' };
      set((state) => ({
        saving: false,
        selectedInvoice: state.selectedInvoice
          ? { ...state.selectedInvoice, ...patch }
          : state.selectedInvoice,
        invoices: patchListItem(state.invoices, id, patch),
      }));
      useToastStore.getState().addToast(t('invoices:messages.sent'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  markPaid: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await postApiV1InvoicesIdPay(id);
      const patch: Partial<InvoiceDto> = { status: 'Paid' };
      set((state) => ({
        saving: false,
        selectedInvoice: state.selectedInvoice
          ? { ...state.selectedInvoice, ...patch }
          : state.selectedInvoice,
        invoices: patchListItem(state.invoices, id, patch),
      }));
      useToastStore.getState().addToast(t('invoices:messages.paid'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  deleteInvoice: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await deleteApiV1InvoicesId(id);
      set((state) => ({
        saving: false,
        selectedInvoice: state.selectedInvoice?.id === id ? null : state.selectedInvoice,
        invoices: state.invoices.filter((invoice) => invoice.id !== id),
      }));
      useToastStore.getState().addToast(t('invoices:messages.deleted'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  cancelInvoice: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await postApiV1InvoicesIdCancel(id);
      const patch: Partial<InvoiceDto> = { status: 'Cancelled' };
      set((state) => ({
        saving: false,
        selectedInvoice: state.selectedInvoice
          ? { ...state.selectedInvoice, ...patch }
          : state.selectedInvoice,
        invoices: patchListItem(state.invoices, id, patch),
      }));
      useToastStore.getState().addToast(t('invoices:messages.cancelled'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  updateInvoice: async (id: string, data: UpdateInvoiceRequest) => {
    set({ saving: true, error: null });
    try {
      const updated = await putApiV1InvoicesId(id, data);
      set((state) => ({
        saving: false,
        selectedInvoice: updated,
        invoices: patchListItem(state.invoices, id, listPatchFrom(updated)),
      }));
      useToastStore.getState().addToast(t('invoices:messages.updated'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  addPosition: async (invoiceId: string, data: AddInvoicePositionRequest) => {
    set({ saving: true, error: null });
    try {
      await postApiV1InvoicesInvoiceIdPositions(invoiceId, data);
      await refreshInvoice(invoiceId);
      useToastStore.getState().addToast(t('invoices:positions.messages.added'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  updatePosition: async (
    invoiceId: string,
    positionId: string,
    data: UpdateInvoicePositionRequest,
  ) => {
    set({ saving: true, error: null });
    try {
      await putApiV1InvoicesInvoiceIdPositionsPositionId(invoiceId, positionId, data);
      await refreshInvoice(invoiceId);
      useToastStore.getState().addToast(t('invoices:positions.messages.updated'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  deletePosition: async (invoiceId: string, positionId: string) => {
    set({ saving: true, error: null });
    try {
      await deleteApiV1InvoicesInvoiceIdPositionsPositionId(invoiceId, positionId);
      await refreshInvoice(invoiceId);
      useToastStore.getState().addToast(t('invoices:positions.messages.deleted'), 'success');
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  reorderPositions: async (invoiceId: string, positionIds: string[]) => {
    set({ saving: true, error: null });
    try {
      await putApiV1InvoicesInvoiceIdPositionsReorder(invoiceId, { positionIds });
      await refreshInvoice(invoiceId);
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  downloadPdf: async (
    id: string,
    invoiceNumber?: string | null,
    billingInterval?: string | null,
  ) => {
    set({ downloadingPdf: true });
    try {
      await downloadInvoicePdf(id, invoiceNumber, billingInterval);
      useToastStore.getState().addToast(
        t('invoices:messages.pdfDownloadSuccess'),
        'success',
      );
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : t('invoices:messages.pdfDownloadError');
      useToastStore.getState().addToast(message, 'error');
    } finally {
      set({ downloadingPdf: false });
    }
  },
  };
});
